import { useGreaterAreaContext } from '@/pages/greater-areas/useGreaterAreaContext';
import { EmptyTableState, Loading, RichTextContent } from '@startupquickstart/frontend';
import { FileText, Globe, MapPin } from 'lucide-react';
import { formatDate } from '@startupquickstart/frontend';

export function GreaterAreaOverviewTab() {
  const { greaterArea, isLoading } = useGreaterAreaContext();

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loading />
      </div>
    );
  }

  if (!greaterArea) return null;

  const hasCoordinates =
    greaterArea.latitude !== undefined &&
    greaterArea.latitude !== null &&
    greaterArea.longitude !== undefined &&
    greaterArea.longitude !== null;

  return (
    <div className="space-y-6">
      {/* Two-column content */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left column */}
        <div className="lg:col-span-3 space-y-6">
          {/* About card */}
          <div className="rounded-xl border bg-card shadow-sm">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium tracking-tight text-zinc-900">About</h2>
              <p className="text-sm text-muted-foreground">
                Greater area description and details
              </p>
            </div>
            <div className="p-6">
              {greaterArea.description ? (
                <RichTextContent content={greaterArea.description} />
              ) : (
                <EmptyTableState
                  icon={<FileText className="h-6 w-6" />}
                  title="No description"
                  description="Edit to add a description"
                />
              )}
            </div>
            <div className="border-t px-6 py-4 text-sm text-muted-foreground flex flex-wrap gap-x-6 gap-y-1">
              <span>Created: {formatDate(greaterArea.createdAt)}</span>
              <span>Updated: {formatDate(greaterArea.updatedAt)}</span>
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Location card */}
          <div className="rounded-xl border bg-card shadow-sm">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium tracking-tight text-zinc-900">Location</h2>
              <p className="text-sm text-muted-foreground">Geographic details</p>
            </div>
            <div className="p-6">
              {greaterArea.country || greaterArea.state || greaterArea.city ? (
                <div className="divide-y">
                  {greaterArea.country && (
                    <div className="py-3 first:pt-0">
                      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5 flex items-center gap-1.5">
                        <Globe className="h-3.5 w-3.5 shrink-0" />
                        Country
                      </div>
                      <div className="text-sm">{greaterArea.country}</div>
                    </div>
                  )}
                  {greaterArea.state && (
                    <div className="py-3">
                      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
                        State
                      </div>
                      <div className="text-sm">{greaterArea.state}</div>
                    </div>
                  )}
                  {greaterArea.city && (
                    <div className="py-3">
                      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
                        City
                      </div>
                      <div className="text-sm">{greaterArea.city}</div>
                    </div>
                  )}
                </div>
              ) : (
                <EmptyTableState
                  icon={<MapPin className="h-6 w-6" />}
                  title="No location data"
                  description="Edit this greater area to add location details"
                />
              )}
            </div>
          </div>

          {/* Coordinates card */}
          {hasCoordinates && (
            <div className="rounded-xl border bg-card shadow-sm">
              <div className="px-6 py-4 border-b">
                <h2 className="text-lg font-medium tracking-tight text-zinc-900">Coordinates</h2>
                <p className="text-sm text-muted-foreground">Geographic coordinates</p>
              </div>
              <div className="p-6">
                <div className="divide-y">
                  <div className="py-3 first:pt-0">
                    <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5 flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5 shrink-0" />
                      Latitude
                    </div>
                    <div className="text-sm">{greaterArea.latitude}</div>
                  </div>
                  <div className="py-3">
                    <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5 flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5 shrink-0" />
                      Longitude
                    </div>
                    <div className="text-sm">{greaterArea.longitude}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
